import { DatePipe } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { Store } from "@ngrx/store";
import { ToasterService } from "angular2-toaster";
import "rxjs/add/operator/map";
import "rxjs/add/operator/startWith";
import swal from "sweetalert2";
import * as fromRootStore from "../../../reducers";
import { Combo2Service, MenuMappingService, SalesInvoiceService, SalesOrderService } from "../../../services";
import { WhWorkOrderService } from "../../../services/whWorkOrder.service";

@Component({
  selector: "addCdcEstimationNew",
  templateUrl: "./addCdcEstimationNew.html",
})
export class addCdcEstimationNew {
  data: any;
  mode: string;
  saveEvent: any;
  isLoading: boolean = false;
  showForm: boolean = false;

  // Lists for dropdowns and table data
  completedWorkOrdersList: any[] = [];
  scannedDetailsList: any[] = [];
  selectedWorkOrder: any = null;

  // Summary data
  summaryData: any = {
    totalItems: 0,
    completedItems: 0,
    availableItems: 0,
    totalPieces: 0,
    completedPieces: 0,
    availablePieces: 0,
    cancelledItems: 0,
    cancelledPieces: 0
  };

  @Input() set params(val: any[]) {
    this.mode = val["mode"];
    let data = Object.assign({}, val["data"]);
    this.data = data;
    console.log('addCdcEstimation data :: ', this.data);
    this.saveEvent = val["data"].tabevent;
  }

  @Output() tabUpdateEvent = new EventEmitter();

  constructor(
    public store: Store<fromRootStore.State>,
    public datePipe: DatePipe,
    public toasterService: ToasterService,
    public MappingService: MenuMappingService,
    private salesinvservice: SalesInvoiceService,
    private combo: Combo2Service,
    private whWorkOrderService: WhWorkOrderService,
    private Salesservice: SalesOrderService,
  ) {
    this.data = {};
  }

  ngOnInit() {
    this.getCompletedWorkOrders();
  }

  ngAfterViewInit() {
    // Focus logic if needed
  }

  // Get completed work orders list
  getCompletedWorkOrders() {
    this.isLoading = true;

    this.whWorkOrderService.getCompletedWorkOrders()
      .then((res: any) => {
        this.isLoading = false;

        if (res && res.success) {
          this.completedWorkOrdersList = res.data || [];
        } else {
          this.completedWorkOrdersList = [];
          this.toasterService.pop({
            bodyOutputType: 1,
            type: "warning",
            title: "WARNING",
            body: res.message || "Failed to load completed work orders",
          });
        }
      })
      .catch((error: any) => {
        this.isLoading = false;
        this.completedWorkOrdersList = [];

        let errorMessage = "Failed to load completed work orders";
        try {
          const errorResponse = JSON.parse(error._body);
          errorMessage = errorResponse.message || errorMessage;
        } catch (e) {
          // Use default message
        }

        this.toasterService.pop({
          bodyOutputType: 1,
          type: "error",
          title: "ERROR",
          body: errorMessage,
        });
      });
  }

  // Handle proceed button click
  onProceedWorkOrder(workOrder: any) {
    this.selectedWorkOrder = workOrder;
    this.getScannedDetails(workOrder.wh_wo_ass_id);
    this.showForm = true;
  }

  // Get scanned details for selected work order
  getScannedDetails(whWoAssId: number) {
    this.isLoading = true;

    this.whWorkOrderService.getScannedDetails(whWoAssId)
      .then((res: any) => {
        this.isLoading = false;

        if (res && res.success) {
          this.scannedDetailsList = res.data || [];

          // Mark all items as completed by default (user will cancel if needed)
          this.scannedDetailsList.forEach(item => {
            item.is_cdc_estimated = 1; // Mark as completed by default
            item.is_cancelled = 0; // Not cancelled by default
          });

          // Update summary
          this.updateSummary();
        } else {
          this.scannedDetailsList = [];
          this.toasterService.pop({
            bodyOutputType: 1,
            type: "warning",
            title: "WARNING",
            body: res.message || "Failed to load scanned details",
          });
        }
      })
      .catch((error: any) => {
        this.isLoading = false;
        this.scannedDetailsList = [];

        let errorMessage = "Failed to load scanned details";
        try {
          const errorResponse = JSON.parse(error._body);
          errorMessage = errorResponse.message || errorMessage;
        } catch (e) {
          // Use default message
        }

        this.toasterService.pop({
          bodyOutputType: 1,
          type: "error",
          title: "ERROR",
          body: errorMessage,
        });
      });
  }

  // Cancel CDC estimation for an item with bag-based logic
  cancelEstimationItem(detailItem: any) {
    // Check if item has bag_no and bag_id
    const hasBagInfo = detailItem.bag_no && detailItem.bag_id;

    // Find related items for bag-based cancellation
    let itemsToCancel = [];
    let confirmMessage = '';

    if (hasBagInfo) {
      // Bag-based cancellation: find all items with same bag_id
      itemsToCancel = this.scannedDetailsList.filter(item =>
        item.bag_id === detailItem.bag_id && item.is_cancelled != 1
      );

      confirmMessage = `Cancel entire bag ${detailItem.bag_no}?\n\nThis will cancel ${itemsToCancel.length} item(s) in this bag.`;
    } else {
      // Size-based cancellation: cancel only this item
      itemsToCancel = [detailItem];

      const itemIdentifier = detailItem.barcode || `Item ${detailItem.wh_wo_assigned_det_id}`;
      confirmMessage = `Cancel this item?\n\nThis will cancel ${itemIdentifier}`;
    }

    // Show confirmation dialog
    swal({
      title: hasBagInfo ? "Cancel Entire Bag?" : "Cancel Item?",
      text: confirmMessage,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, cancel it!",
      cancelButtonText: "No, keep it",
    }).then(() => {
      // Mark all items in the list as cancelled
      itemsToCancel.forEach(item => {
        item.is_cancelled = 1;
        item.is_cdc_estimated = 0;
        item.cancelled_at = new Date();
      });

      // Update summary
      this.updateSummary();

      const successMessage = hasBagInfo ?
        `Bag ${detailItem.bag_no} with ${itemsToCancel.length} items cancelled successfully` :
        `Item cancelled successfully`;

      this.toasterService.pop({
        bodyOutputType: 1,
        type: "success",
        title: "SUCCESS",
        body: successMessage,
      });
    });
  }

  // Restore estimation item with bag-based logic
  restoreEstimationItem(detailItem: any) {
    // Check if item has bag_no and bag_id
    const hasBagInfo = detailItem.bag_no && detailItem.bag_id;

    // Find related items for bag-based restoration
    let itemsToRestore = [];
    let confirmMessage = '';

    if (hasBagInfo) {
      // Bag-based restoration: find all items with same bag_id that are cancelled
      itemsToRestore = this.scannedDetailsList.filter(item =>
        item.bag_id === detailItem.bag_id && item.is_cancelled == 1
      );

      confirmMessage = `Restore entire bag ${detailItem.bag_no}?\n\nThis will restore ${itemsToRestore.length} item(s) in this bag.`;
    } else {
      // Size-based restoration: restore only this item
      itemsToRestore = [detailItem];

      const itemIdentifier = detailItem.barcode || `Item ${detailItem.wh_wo_assigned_det_id}`;
      confirmMessage = `Restore this item?\n\nThis will restore ${itemIdentifier}`;
    }

    // Show confirmation for bag restoration (skip for single item)
    if (hasBagInfo) {
      swal({
        title: "Restore Entire Bag?",
        text: confirmMessage,
        type: "info",
        showCancelButton: true,
        confirmButtonText: "Yes, restore it!",
        cancelButtonText: "No, keep cancelled",
      }).then(() => {
        this.performRestore(itemsToRestore, detailItem);
      });
    } else {
      this.performRestore(itemsToRestore, detailItem);
    }
  }

  // Helper method to perform the restore operation
  private performRestore(itemsToRestore: any[], detailItem: any) {
    const hasBagInfo = detailItem.bag_no && detailItem.bag_id;

    // Mark all items as restored
    itemsToRestore.forEach(item => {
      item.is_cancelled = 0;
      item.is_cdc_estimated = 1;
      item.cancelled_at = null;
    });

    // Update summary
    this.updateSummary();

    const successMessage = hasBagInfo ?
      `Bag ${detailItem.bag_no} with ${itemsToRestore.length} items restored successfully` :
      `Item restored successfully`;

    this.toasterService.pop({
      bodyOutputType: 1,
      type: "success",
      title: "SUCCESS",
      body: successMessage,
    });
  }

  // Enhanced updateSummary method with better categorization
  updateSummary() {
    const totalItems = this.scannedDetailsList.length;
    const cancelledItems = this.scannedDetailsList.filter(item => item.is_cancelled == 1);
    const completedItems = this.scannedDetailsList.filter(item =>
      item.is_cdc_estimated == 1 && item.is_cancelled != 1
    );

    // Calculate pieces (sum of all size quantities for each item)
    let totalPieces = 0;
    let completedPieces = 0;
    let cancelledPieces = 0;

    this.scannedDetailsList.forEach(item => {
      const itemPieces = this.calculateItemPieces(item);
      totalPieces += itemPieces;

      if (item.is_cancelled == 1) {
        cancelledPieces += itemPieces;
      } else if (item.is_cdc_estimated == 1) {
        completedPieces += itemPieces;
      }
    });

    this.summaryData = {
      totalItems: totalItems,
      completedItems: completedItems.length,
      cancelledItems: cancelledItems.length,
      availableItems: completedItems.length,
      totalPieces: totalPieces,
      completedPieces: completedPieces,
      cancelledPieces: cancelledPieces,
      availablePieces: completedPieces
    };
  }

  // Helper method to calculate pieces for an item
  private calculateItemPieces(item: any): number {
    const sizeFields = ['F36', 'F38', 'F40', 'F42', 'F44', 'F46', 'H36', 'H38', 'H40', 'H42', 'H44', 'H46'];
    let pieces = 0;

    sizeFields.forEach(size => {
      const value = item[size];
      if (value && value !== '-' && !isNaN(value) && value > 0) {
        pieces += parseInt(value, 10);
      }
    });

    return pieces;
  }

  // Enhanced visual feedback methods for better user experience
  highlightBagItems(bagId: string, highlight: boolean) {
    if (!bagId) return;

    // Add visual feedback to show which items will be affected
    this.scannedDetailsList.forEach(item => {
      if (item.bag_id === bagId) {
        item.highlightForBagAction = highlight;
      }
    });
  }

  // Method to show item type in the UI
  getItemType(item: any): string {
    return (item.bag_no && item.bag_id) ? 'bag' : 'size';
  }

  // Method to count related items for display
  getRelatedItemsCount(item: any): number {
    if (!item.bag_no || !item.bag_id) return 1;

    return this.scannedDetailsList.filter(relatedItem =>
      relatedItem.bag_id === item.bag_id
    ).length;
  }

  // Go back to work orders list
  goBackToList() {
    this.showForm = false;
    this.selectedWorkOrder = null;
    this.scannedDetailsList = [];
    this.summaryData = {
      totalItems: 0,
      completedItems: 0,
      availableItems: 0,
      totalPieces: 0,
      completedPieces: 0,
      availablePieces: 0,
      cancelledItems: 0,
      cancelledPieces: 0
    };
    this.getCompletedWorkOrders();
  }

  // Submit CDC estimation
  submitEstimation() {
    // Prepare all items data with their statuses
    const allItems = this.scannedDetailsList.map(item => ({
      wh_wo_assigned_det_id: item.wh_wo_assigned_det_id,
      is_cancelled: item.is_cancelled || 0,
      is_cdc_estimated: item.is_cdc_estimated || 0,
      barcode: item.barcode,
      bag_id: item.bag_id,
      bag_no: item.bag_no
    }));

    swal({
      title: "Submit CDC Estimation?",
      text: `This will submit estimation for all ${allItems.length} items`,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, submit it!",
      cancelButtonText: "No, continue working",
    }).then(() => {
      this.isLoading = true;

      const submitData = {
        wh_wo_ass_id: this.selectedWorkOrder.wh_wo_ass_id,
        estimated_items: allItems
      };

      this.whWorkOrderService.updateCdcEstimation(submitData)
        .then((res: any) => {
          this.isLoading = false;

          if (res.success) {
            this.toasterService.pop({
              bodyOutputType: 1,
              type: "success",
              title: "SUCCESS",
              body: res.message,
            });

            this.convertToCdc(res.cdc_est_id);
          } else {
            this.toasterService.pop({
              bodyOutputType: 1,
              type: "error",
              title: "ERROR",
              body: res.message,
            });
          }
        })
        .catch((error: any) => {
          this.isLoading = false;

          let errorMessage = "Failed to submit CDC estimation";
          try {
            const errorResponse = JSON.parse(error._body);
            errorMessage = errorResponse.message || errorMessage;
          } catch (e) {
            // Use default message
          }

          this.toasterService.pop({
            bodyOutputType: 1,
            type: "error",
            title: "ERROR",
            body: errorMessage,
          });
        });
    });
  }

  // Track by function for better performance
  trackByWorkOrderId(index: number, workOrder: any): any {
    return workOrder.wh_wo_ass_id || index;
  }

  // Track by function for scanned details
  trackByDetailId(index: number, detail: any): any {
    return detail.wh_wo_assigned_det_id || index;
  }

  convertToCdc(cdc_est_id) {
    let that = this;
    let params = {
      cdc_est_id: cdc_est_id
    };
    that.whWorkOrderService
      .getCdcEstimationDetails(params)
      .then(function (res: any) {
        that.isLoading = true;

        if (res.success) {
          const saveCdcParams = JSON.parse(JSON.stringify(res.data));
          saveCdcParams.cdc_est_id = cdc_est_id
          saveCdcParams['dcinvoice'] = JSON.stringify(saveCdcParams['dcinvoice']);
          that.Salesservice
            .Savecounterdcentry(saveCdcParams)
            .then(function (saveCdcRes: any) {

              if (saveCdcRes.success) {
                that.whWorkOrderService.updateCdcEstimationStatus({ cdc_est_id, status: 'CONVERTED' }).then(function (updateCdcRes: any) {
                  if (updateCdcRes.success) {
                    that.toasterService.pop({
                      bodyOutputType: 1,
                      type: 'success',
                      title: 'SUCCESS',
                      body: 'Converted successfully'
                    });

                    // Close tab
                    let tabObj = {
                      action: "CloseTab",
                      params: {},
                    };
                    that.tabUpdateEvent.emit(tabObj);
                  } else {
                    that.toasterService.pop({
                      bodyOutputType: 1,
                      type: 'error',
                      title: 'ERROR',
                      body: 'Error while updating cdc estimation status'
                    });
                  }
                })
              } else {
                that.toasterService.pop({
                  bodyOutputType: 1,
                  type: 'error',
                  title: 'ERROR',
                  body: 'Error while creating counter dc'
                });
              }

            })
        } else {
          that.toasterService.pop({
            bodyOutputType: 1,
            type: 'error',
            title: 'ERROR',
            body: 'Error while getting cdc estimation data'
          });
        }

      });
  }
}


// WEBPACK FOOTER //
// /Users/afsarahamed/Documents/symatis/projects/hyatt-angularweb/src/app/components/phase1/CdcEstimation/addCdcEstimationNew.ts